import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { getContentByIdOptions, getFocusedFeedInfiniteOptions, getMyContentInfiniteOptions } from '@/utils/queryOptions';
import { TGetFocusedFeedParams } from '@/services/api/requests/content';

// The focused "loop" stream, seeded by the card the user tapped.
//
// Fetching here records nothing — GET /focused-feed is explicitly read-only. A view is
// only reported when the user actually consumes an item, via useFocusedViewTracker.
export type TFocusedFeedSource = 'focused' | 'library';

const useFocusedFeed = (
  { anchorId, tab, limit }: TGetFocusedFeedParams,
  source: TFocusedFeedSource = 'focused',
) => {
  const queryClient = useQueryClient();

  // /focused-feed intentionally mixes creators. A loop entered from the creator library
  // must keep using the library's canonical /content/me query, including on later pages.
  const focusedQuery = useInfiniteQuery({
    ...getFocusedFeedInfiniteOptions({ anchorId, tab, limit }),
    enabled: source === 'focused' && Boolean(anchorId),
  });
  const libraryQuery = useInfiniteQuery({
    ...getMyContentInfiniteOptions(),
    enabled: source === 'library',
  });
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    source === 'library' ? libraryQuery : focusedQuery;

  const items = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data?.pages]);

  // Seed the detail cache so "Read Full Article" opens instantly. Guarded on `details`
  // in case the focused feed trims card payloads.
  useEffect(() => {
    items.forEach((item) => {
      if (item.details) queryClient.setQueryData(getContentByIdOptions(item.id).queryKey, item);
    });
  }, [items, queryClient]);

  return { items, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage };
};

export default useFocusedFeed;
